/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import { FC } from "react"
import { Row, Col, Divider } from "grab-ui"
import { Button } from "@duxton-web/ui"
import {
  ChevronLeftUniversal,
  ChevronRightUniversal,
  RefreshTiny,
} from "@duxton-web/icons"

import { cardStyle } from "styles/common"
import { useTranslationsMap } from "common/utils/i18n"
import { stepsActionDividerContainer, stepsActionStyle } from "./style"

interface StepsActionProps {
  message?: string
  onNext?(): any
  onPrev?(): any
  onReset?(): any
  onSubmit?(): any
  showNext?: boolean
  showPrev?: boolean
  showSubmit?: boolean
  showReset?: boolean
  disabledNext?: boolean
  disabledPrev?: boolean
  errorMessage?: string
}
export const StepsAction: FC<StepsActionProps> = ({
  message,
  onNext = () => {},
  onPrev = () => {},
  onReset = () => {},
  onSubmit = () => {},
  showNext = true,
  showPrev = true,
  showSubmit = false,
  showReset = false,
  disabledNext = false,
  disabledPrev = false,
  errorMessage,
}: StepsActionProps) => {
  const textMap = useTranslationsMap({
    next: "menu.menuGroup.create.next",
    prev: "menu.menuGroup.create.previous",
    publish: "menu.menuGroup.create.publish",
  })

  return (
    <div
      css={css`
        ${cardStyle}
        ${stepsActionStyle}
      `}
      className={errorMessage ? "error" : ""}
    >
      {errorMessage ? (
        <p className="error">{errorMessage}</p>
      ) : (
        message && <p>{message}</p>
      )}
      <Row type="flex" justify="end" gutter={20}>
        {showReset && (
          <Col>
            <div className="button-container reset">
              <Button
                data-testid="btn-reset"
                danger
                icon={<RefreshTiny style={{ fontSize: 16 }} />}
                onClick={onReset}
              >
                Reset Menu
              </Button>
            </div>
          </Col>
        )}
        {showReset && (
          <Col css={stepsActionDividerContainer}>
            <Divider type="vertical" />
          </Col>
        )}
        {showPrev && (
          <Col>
            <div className="button-container">
              <Button
                data-testid="btn-prev"
                icon={<ChevronLeftUniversal style={{ fontSize: 16 }} />}
                onClick={onPrev}
                disabled={disabledPrev}
              >
                {textMap.prev}
              </Button>
            </div>
          </Col>
        )}
        {showNext && (
          <Col>
            <div className="button-container">
              <Button
                data-testid="btn-next"
                type="primary"
                onClick={onNext}
                disabled={disabledNext}
              >
                {textMap.next}
                <ChevronRightUniversal style={{ fontSize: 16 }} />
              </Button>
            </div>
          </Col>
        )}
        {showSubmit && (
          <Col>
            <div className="button-container">
              <Button
                data-testid="btn-submit"
                type="primary"
                onClick={onSubmit}
              >
                {textMap.publish}
              </Button>
            </div>
          </Col>
        )}
      </Row>
    </div>
  )
}
